import React from "react";
import { MoreVertical } from "lucide-react";

export interface MockupRecommendation {
  name: string;
  subtext: string;
  cpuAvg?: string;
  cpuPct?: string;
  memAvg?: string;
  memPct?: string;
  scale?: string;
  tasks?: string;
  size?: string;
  current: string;
  recommended: string;
  savings: string;
}

interface Props {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  recommendations: readonly MockupRecommendation[];
  footerText: string;
  compact?: boolean;
}

export function ProductRecommendationsMockup({
  title,
  icon: Icon,
  recommendations,
  footerText,
  compact = false,
}: Props): React.ReactElement {
  const showCpu = recommendations.some((r) => r.cpuPct !== undefined);
  const showMemory = recommendations.some((r) => r.memAvg !== undefined);
  const showScale = recommendations.some((r) => r.scale !== undefined);
  const showTasks = recommendations.some((r) => r.tasks !== undefined);
  const showSize = recommendations.some((r) => r.size !== undefined);

  return (
    <div className="rounded-2xl border bg-background/80 shadow-xl overflow-hidden text-sm select-none">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background/50">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-primary" />
          <span className="font-semibold text-sm">{title}</span>
        </div>
        <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
          {recommendations.length} found
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={`w-full ${compact ? "" : "min-w-[700px]"}`}>
          <thead>
            <tr className="border-b bg-surface/30 text-xs text-surface-foreground/50 font-medium">
              <th className="text-left px-4 py-2">Resource</th>
              {showSize && (
                <th className="text-center px-2 py-2 w-20">Size</th>
              )}
              {showCpu && (
                <th className="text-center px-2 py-2 w-28">CPU</th>
              )}
              {showMemory && (
                <th className="text-center px-2 py-2 w-28">Memory</th>
              )}
              {showScale && (
                <th className="text-center px-2 py-2 w-16">Scale</th>
              )}
              {showTasks && (
                <th className="text-center px-2 py-2 w-16">Tasks</th>
              )}
              <th className="text-center px-2 py-2 w-20">Current</th>
              <th className="text-center px-2 py-2 w-24">Recommended</th>
              <th className="text-center px-2 py-2 w-14">Savings</th>
              <th className={compact ? "w-20 px-2 py-2" : "w-24 px-2 py-2"} />
            </tr>
          </thead>
          <tbody>
            {recommendations.map((r) => (
              <tr
                key={r.name}
                className="border-b last:border-0 transition-colors hover:bg-surface/30"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="font-mono text-xs text-foreground block">
                    {r.name}
                  </span>
                  <span className="text-xs text-surface-foreground/40 mt-0.5 block">
                    {r.subtext}
                  </span>
                </td>
                {showSize && (
                  <td className="px-2 py-3 text-center align-middle">
                    <span className="font-mono text-xs text-foreground whitespace-nowrap">
                      {r.size}
                    </span>
                  </td>
                )}
                {showCpu && (
                  <td className="px-2 py-3 text-center">
                    <span className="font-mono text-xs text-foreground block">
                      {r.cpuPct}
                    </span>
                    {!compact && (
                      <span className="font-mono text-xs text-surface-foreground/40 block">
                        avg {r.cpuAvg}
                      </span>
                    )}
                  </td>
                )}
                {showMemory && (
                  <td className="px-2 py-3 text-center">
                    <span className="font-mono text-xs text-foreground block">
                      {r.memPct}
                    </span>
                    {!compact && (
                      <span className="font-mono text-xs text-surface-foreground/40 block">
                        avg {r.memAvg}
                      </span>
                    )}
                  </td>
                )}
                {showScale && (
                  <td className="px-2 py-3 text-center">
                    <span className="font-mono text-xs text-foreground">
                      {r.scale}
                    </span>
                  </td>
                )}
                {showTasks && (
                  <td className="px-2 py-3 text-center">
                    <span className="font-mono text-xs text-foreground">
                      {r.tasks}
                    </span>
                  </td>
                )}
                <td className="px-2 py-3 text-center align-middle">
                  <span className="font-mono text-xs text-foreground whitespace-nowrap">
                    {r.current}
                  </span>
                </td>
                <td className="px-2 py-3 text-center align-middle">
                  <span className="font-mono text-xs text-primary font-medium whitespace-nowrap">
                    {r.recommended}
                  </span>
                </td>
                <td className="px-2 py-3 text-center align-middle">
                  <span className="text-xs text-green-600 dark:text-green-400 font-semibold">
                    {r.savings}
                  </span>
                </td>
                <td className="px-2 py-3 text-center">
                  <span className="inline-flex items-center gap-1">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-primary text-primary-foreground">
                      Generate PR
                    </span>
                    {!compact && (
                      <span className="p-1 rounded-md text-surface-foreground/30">
                        <MoreVertical className="size-4" />
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 bg-surface/20 border-t text-xs text-surface-foreground/40 flex justify-between">
        <span>{footerText}</span>
        <span>Updated 2 min ago</span>
      </div>
    </div>
  );
}
